import React, {
    createContext,
    useContext,
    useState,
    useEffect,
    useCallback,
} from "react";
import { useSelector } from "react-redux";
import {
    requestPermission,
    subscribeToForegroundMessages,
    onServiceWorkerMessageListener,
} from "../notifications/firebase";
import { BackOfficeNotificationItem, NotificationCategory } from "notifications/types";
import { BackOfficeNotificationListReq } from "domain/payload/request/backOfficeNotification/BackOfficeNotificationReq";
import BackOfficeNotificationService from "service/BackOfficeNotificationService";
import AuthService from "service/AuthService";
import { playNotificationSound } from "utils/notificationSounds";
import NotificationPermissionModal from "../component/NotificationPermissionModal";

export interface CategoryMeta {
    label: string;
    icon: string;
    badgeBg: string;
    badgeText: string;
    iconBg: string;
    iconColor: string;
}

export const NOTIFICATION_CATEGORIES: Record<
    NotificationCategory,
    CategoryMeta
> = {
    MONITORING: {
        label: "Monitoring",
        icon: "fa-solid fa-chart-line",
        badgeBg: "bg-blue-50 border-blue-200",
        badgeText: "text-blue-700",
        iconBg: "bg-blue-100",
        iconColor: "text-blue-600",
    },
    ALERT: {
        label: "Alert",
        icon: "fa-solid fa-triangle-exclamation",
        badgeBg: "bg-amber-50 border-amber-200",
        badgeText: "text-amber-700",
        iconBg: "bg-amber-100",
        iconColor: "text-amber-600",
    },
    SYSTEM: {
        label: "System",
        icon: "fa-solid fa-server",
        badgeBg: "bg-purple-50 border-purple-200",
        badgeText: "text-purple-700",
        iconBg: "bg-purple-100",
        iconColor: "text-purple-600",
    },
    CAMPAIGN: {
        label: "Campaign",
        icon: "fa-solid fa-bullhorn",
        badgeBg: "bg-pink-50 border-pink-200",
        badgeText: "text-pink-700",
        iconBg: "bg-pink-100",
        iconColor: "text-pink-600",
    },
    WELCOME: {
        label: "Welcome",
        icon: "fa-solid fa-hand-wave",
        badgeBg: "bg-emerald-50 border-emerald-200",
        badgeText: "text-emerald-700",
        iconBg: "bg-emerald-100",
        iconColor: "text-emerald-600",
    },
    ANNOUNCEMENT: {
        label: "Announcement",
        icon: "fa-solid fa-bullhorn",
        badgeBg: "bg-cyan-50 border-cyan-200",
        badgeText: "text-cyan-700",
        iconBg: "bg-cyan-100",
        iconColor: "text-cyan-600",
    },
};

interface NotificationContextType {
    notifications: BackOfficeNotificationItem[];
    unreadCount: number;
    loading: boolean;
    total: number;
    totalPages: number;
    page: number;
    lastNewNotificationAt: number;
    fetchNotifications: (params?: BackOfficeNotificationListReq) => Promise<void>;
    fetchUnreadCount: (silent?: boolean) => Promise<void>;
    markAsRead: (id: string) => Promise<void>;
    markAllAsRead: () => Promise<void>;
    deleteNotification: (id: string) => Promise<void>;
    formatRelativeTime: (dateString: string) => string;
    openPermissionModal: () => void;
}

const NotificationContext = createContext<NotificationContextType | undefined>(
    undefined
);

export const NotificationProvider: React.FC<{ children: React.ReactNode }> = ({
    children,
}) => {
    const authState = useSelector((state: any) => state.auth);
    const token =
        authState?.profile?.data?.token ||
        authState?.profile?.token ||
        authState?.token;
    const service = React.useMemo(() => BackOfficeNotificationService(), []);

    // Clear any previously cached dummy notifications from localStorage
    useEffect(() => {
        try {
            localStorage.removeItem("transmora_dummy_notifications_v1");
            localStorage.removeItem("transmora_dummy_notifications_v2");
        } catch {
            // ignore
        }
    }, []);

    const [notifications, setNotifications] = useState<BackOfficeNotificationItem[]>([]);
    const [unreadCount, setUnreadCount] = useState<number>(0);
    const [loading, setLoading] = useState<boolean>(false);
    const [total, setTotal] = useState<number>(0);
    const [page, setPage] = useState<number>(1);
    const [totalPages, setTotalPages] = useState<number>(1);
    const [lastNewNotificationAt, setLastNewNotificationAt] = useState<number>(0);
    const [isPermissionModalOpen, setIsPermissionModalOpen] = useState<boolean>(false);
    const [isRegisteringToken, setIsRegisteringToken] = useState<boolean>(false);

    const initialFetchDone = React.useRef(false);
    const prevUnreadCountRef = React.useRef<number>(0);

    // Fetch unread count from API
    const fetchUnreadCount = useCallback(async (silent = false) => {
        if (!token) {
            console.warn("[NotificationContext] No token available for unread count");
            setUnreadCount(0);
            return;
        }
        try {
            const resp = await service.backOfficeNotificationGetUnreadCount(token);
            if (resp?.status === "T" && resp?.data) {
                const newCount = resp.data.unreadCount ?? 0;
                setUnreadCount(newCount);

                // Jika bukan inisialisasi awal, tidak dalam mode silent, dan jumlah notifikasi bertambah -> bunyikan suara dan trigger refresh
                if (!silent && initialFetchDone.current && newCount > prevUnreadCountRef.current) {
                    console.log("[NotificationContext] New notification detected via poll! Playing sound...");
                    playNotificationSound();
                    setLastNewNotificationAt(Date.now());
                }

                prevUnreadCountRef.current = newCount;
                initialFetchDone.current = true;
            } else {
                setUnreadCount(0);
            }
        } catch (e) {
            console.warn("Could not fetch unread count from API:", e);
            setUnreadCount(0);
        }
    }, [token, service]);

    // Fetch notifications from API
    const fetchNotifications = useCallback(
        async (params?: BackOfficeNotificationListReq) => {
            if (!token) {
                console.warn("[NotificationContext] No token available for fetchNotifications");
                setNotifications([]);
                setTotal(0);
                return;
            }
            setLoading(true);
            try {
                const resp = await service.backOfficeNotificationList(token, params);
                if (resp?.status === "T" && resp?.data) {
                    const items = resp.data.items || [];
                    setNotifications(items);
                    setTotal(resp.data.total ?? items.length);
                    setPage(resp.data.page ?? 1);
                    setTotalPages(resp.data.totalPages ?? 1);
                } else {
                    setNotifications([]);
                    setTotal(0);
                }
            } catch (e) {
                console.warn("Could not fetch notifications from API:", e);
                setNotifications([]);
                setTotal(0);
            } finally {
                setLoading(false);
            }
        },
        [token, service]
    );

    // Custom Permission Modal Handlers
    const handleAcceptPermission = useCallback(async () => {
        setIsRegisteringToken(true);
        try {
            const fcmToken = await requestPermission(true);
            if (fcmToken && token) {
                await AuthService().updateFcmToken(fcmToken, token);
                console.log("[NotificationContext] FCM token successfully saved to backend!");
            }
            setIsPermissionModalOpen(false);
        } catch (error) {
            console.warn("[NotificationContext] Error requesting permission:", error);
            setIsPermissionModalOpen(false);
        } finally {
            setIsRegisteringToken(false);
        }
    }, [token]);

    const handleDismissPermission = useCallback(() => {
        setIsPermissionModalOpen(false);
    }, []);

    const openPermissionModal = useCallback(() => {
        setIsPermissionModalOpen(true);
    }, []);

    // Initial setup when token is ready
    useEffect(() => {
        if (token) {
            // Hapus flag lama dari sessionStorage agar selalu muncul saat reload selama belum diputuskan
            try {
                sessionStorage.removeItem("tm_notif_modal_dismissed");
            } catch {
                // ignore
            }

            if ("Notification" in window) {
                if (Notification.permission === "granted") {
                    // Jika izin sudah diberikan sebelumnya, sinkronkan FCM token di background tanpa memunculkan modal
                    requestPermission(false)
                        .then((fcmToken) => {
                            console.log("[NotificationContext] Obtained FCM Token:", fcmToken);
                            if (fcmToken) {
                                AuthService()
                                    .updateFcmToken(fcmToken, token)
                                    .then(() => console.log("[NotificationContext] FCM token successfully saved to backend!"))
                                    .catch((e) => {
                                        console.warn("Failed to sync FCM token in NotificationContext:", e);
                                    });
                            }
                        })
                        .catch((err) => {
                            console.warn("Could not retrieve FCM token in NotificationContext:", err);
                        });
                } else if (Notification.permission === "default") {
                    // Hanya mintain ketika berhasil login aja, jika permission belum aktif
                    const justLoggedIn = sessionStorage.getItem("tm_just_logged_in") === "true";
                    if (justLoggedIn) {
                        sessionStorage.removeItem("tm_just_logged_in");
                        const timer = setTimeout(() => {
                            setIsPermissionModalOpen(true);
                        }, 600);
                        return () => clearTimeout(timer);
                    }
                } else if (Notification.permission === "denied") {
                    // Jika user sudah menolak di pengaturan browser, jangan tawarkan lagi
                    console.log("[NotificationContext] Notification permission has been denied by user. Will not prompt again.");
                }
            }
            fetchUnreadCount(true);
        } else {
            setNotifications([]);
            setUnreadCount(0);
            setTotal(0);
            setIsPermissionModalOpen(false);
            initialFetchDone.current = false;
            prevUnreadCountRef.current = 0;
        }
    }, [token, fetchUnreadCount]);

    // Periodic poll for unread count (every 30 seconds)
    // useEffect(() => {
    //     if (!token) return;
    //     const interval = setInterval(() => {
    //         fetchUnreadCount();
    //     }, 30000);
    //     return () => clearInterval(interval);
    // }, [token, fetchUnreadCount]);

    // Periodic poll for unread count (diundur ke 1 menit / 60 detik sebagai background sync berkala)
    useEffect(() => {
        if (!token) return;

        const interval = setInterval(() => {
            fetchUnreadCount();
        }, 60000); // 1 menit

        return () => clearInterval(interval);
    }, [token, fetchUnreadCount]);

    // Real-time listener for incoming push notifications (plays SFX and refreshes unread count)
    useEffect(() => {
        if (!token) return;

        const unsubscribeFCM = subscribeToForegroundMessages((payload) => {
            console.log("[NotificationContext] Foreground push notification received:", payload);

            // Jagaan: Abaikan pesan jika bukan untuk aplikasi CMS (applicationType === "1")
            const appType = payload?.data?.applicationType;
            if (appType && String(appType) !== "1") {
                console.log("[NotificationContext] Ignored foreground message for applicationType:", appType);
                return;
            }

            playNotificationSound();
            fetchUnreadCount(true); // Silent mode: update badge tanpa membunyikan suara lagi karena sudah dibunyikan oleh FCM di atas
            setLastNewNotificationAt(Date.now());

            // Tampilkan native notification banner browser bahkan saat tab sedang aktif (foreground)
            if ("Notification" in window && Notification.permission === "granted") {
                const title = payload?.notification?.title || payload?.data?.title || "TransMore Alert";
                const body = payload?.notification?.body || payload?.data?.message || payload?.data?.body || "New notification received.";
                if ("serviceWorker" in navigator) {
                    navigator.serviceWorker.ready.then((reg) => {
                        reg.showNotification(title, {
                            body,
                            icon: "/logo192.png",
                            badge: "/favicon.ico",
                            tag: payload?.data?.id || `fcm-${Date.now()}`,
                            data: payload?.data,
                        });
                    }).catch(() => {
                        try {
                            new Notification(title, { body, icon: "/logo192.png" });
                        } catch {
                            // ignore
                        }
                    });
                }
            }
        });

        const unsubscribeSW = onServiceWorkerMessageListener((data) => {
            console.log("[NotificationContext] Service Worker notification event:", data);

            // Jagaan: Abaikan event SW jika bukan untuk aplikasi CMS (applicationType === "1")
            const payloadData = data?.payload?.data || data?.data;
            const appType = payloadData?.applicationType;
            if (appType && String(appType) !== "1") {
                console.log("[NotificationContext] Ignored SW message for applicationType:", appType);
                return;
            }

            if (data?.type === "NOTIFICATION_CLICKED" && payloadData?.url) {
                window.location.href = payloadData.url;
                return;
            }
            playNotificationSound();
            fetchUnreadCount(true); // Silent mode
            setLastNewNotificationAt(Date.now());
        });

        return () => {
            if (typeof unsubscribeFCM === "function") unsubscribeFCM();
            if (typeof unsubscribeSW === "function") unsubscribeSW();
        };
    }, [token, fetchUnreadCount]);

    // Mark as Read
    const markAsRead = async (id: string) => {
        // Optimistic UI update
        setNotifications((prev) =>
            prev.map((n) => (n.id === id ? { ...n, isRead: true } : n))
        );
        setUnreadCount((prev) => {
            const updated = Math.max(0, prev - 1);
            prevUnreadCountRef.current = updated;
            return updated;
        });

        if (token) {
            try {
                await service.backOfficeNotificationMarkAsRead(token, id);
            } catch (e) {
                console.warn("Failed to mark as read on API:", e);
            }
        }
    };

    // Mark All as Read
    const markAllAsRead = async () => {
        // Optimistic UI update
        setNotifications((prev) => prev.map((n) => ({ ...n, isRead: true })));
        setUnreadCount(0);
        prevUnreadCountRef.current = 0;

        if (token) {
            try {
                await service.backOfficeNotificationMarkAllAsRead(token);
            } catch (e) {
                console.warn("Failed to mark all as read on API:", e);
            }
        }
    };

    // Delete notification
    const deleteNotification = async (id: string) => {
        setNotifications((prev) => prev.filter((n) => n.id !== id));
        setTotal((prev) => Math.max(0, prev - 1));

        if (token) {
            try {
                await service.backOfficeNotificationDelete(token, id);
            } catch (e) {
                console.warn("Failed to delete notification on API:", e);
            }
        }
    };

    const formatRelativeTime = (dateString: string): string => {
        try {
            const date = new Date(dateString);
            const now = new Date();
            const diffMs = now.getTime() - date.getTime();
            const diffMinutes = Math.floor(diffMs / (1000 * 60));
            const diffHours = Math.floor(diffMinutes / 60);
            const diffDays = Math.floor(diffHours / 24);

            if (diffMinutes < 1) return "Just now";
            if (diffMinutes < 60) return `${diffMinutes}m ago`;
            if (diffHours < 24) return `${diffHours}h ago`;
            if (diffDays === 1) return "Yesterday";
            if (diffDays < 7) return `${diffDays}d ago`;

            return date.toLocaleDateString("en-US", {
                month: "short",
                day: "numeric",
                year: "numeric",
            });
        } catch {
            return dateString;
        }
    };

    return (
        <NotificationContext.Provider
            value={{
                notifications,
                unreadCount,
                loading,
                total,
                totalPages,
                page,
                lastNewNotificationAt,
                fetchNotifications,
                fetchUnreadCount,
                markAsRead,
                markAllAsRead,
                deleteNotification,
                formatRelativeTime,
                openPermissionModal,
            }}
        >
            {children}
            <NotificationPermissionModal
                isOpen={isPermissionModalOpen}
                onAccept={handleAcceptPermission}
                onDismiss={handleDismissPermission}
                isSubmitting={isRegisteringToken}
            />
        </NotificationContext.Provider>
    );
};

export const useNotifications = () => {
    const context = useContext(NotificationContext);
    if (!context) {
        throw new Error(
            "useNotifications must be used within a NotificationProvider"
        );
    }
    return context;
};

export const BackOfficeNotifications = useNotifications;
